import createApp from "@shopify/app-bridge";
import { Redirect } from "@shopify/app-bridge/actions";
import { SHOPIFY_API_KEY } from "../config/env";

/**
 * Path for App Bridge ADMIN_PATH: must NOT include `/store/{handle}/` — the host already
 * applies store context, otherwise you get `/store/x/store/x/themes/...`.
 */
function adminPathWithoutStorePrefix(adminUrl) {
  const u = new URL(adminUrl);
  if (u.hostname !== "admin.shopify.com") return null;
  const stripped = u.pathname.replace(/^\/store\/[^/]+(?=\/|$)/, "") || "/";
  const path = `${stripped.startsWith("/") ? stripped : `/${stripped}`}${u.search}${u.hash}`;
  return path;
}

/**
 * Theme editor + other Shopify Admin links: prefer real browser navigation with the full URL.
 * App Bridge REMOTE / ADMIN_PATH with a full `/store/...` path often duplicates the store segment.
 */
export function openRemoteUrl(url, options = {}) {
  if (!url) return;
  const { newContext = true } = options;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    parsed = null;
  }

  const isAdminShopifyHost =
    parsed && parsed.hostname === "admin.shopify.com";
  const isMyshopifyAdmin =
    parsed &&
    typeof parsed.hostname === "string" &&
    parsed.hostname.endsWith(".myshopify.com") &&
    parsed.pathname.includes("/admin/");

  if (isAdminShopifyHost || isMyshopifyAdmin) {
    try {
      if (newContext) {
        const opened = window.open(url, "_blank", "noopener,noreferrer");
        if (opened) return;
      }
      const target = window.top ?? window;
      target.location.assign(url);
      return;
    } catch (e) {
      console.warn("[openRemoteUrl] direct admin navigation failed, trying App Bridge:", e);
    }

    if (isAdminShopifyHost) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const host = urlParams.get("host");
        const bridgePath = adminPathWithoutStorePrefix(url);
        if (host && SHOPIFY_API_KEY && bridgePath) {
          const app = createApp({ apiKey: SHOPIFY_API_KEY, host });
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.ADMIN_PATH, {
            path: bridgePath,
            newContext,
          });
          return;
        }
      } catch (e2) {
        console.warn("[openRemoteUrl] App Bridge ADMIN_PATH fallback:", e2);
      }
    }

    try {
      const target = window.top ?? window;
      target.location.assign(url);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get("host");
    if (host && SHOPIFY_API_KEY) {
      const app = createApp({ apiKey: SHOPIFY_API_KEY, host });
      const redirect = Redirect.create(app);
      redirect.dispatch(Redirect.Action.REMOTE, {
        url,
        newContext,
      });
      return;
    }
  } catch (e) {
    console.warn("[openRemoteUrl] App Bridge redirect:", e);
  }
  try {
    if (window.top && window.top !== window) {
      window.top.location.href = url;
    } else {
      window.location.href = url;
    }
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
